// Helpers for SeriesPickerModal (Comparison tab instrument / analyst pickers).

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

// Map each series name (the value of `seriesField` on a data point, e.g. 'instrument'
// or 'personnel') to the sorted, de-duplicated, non-blank locations it appears at.
// Locations are stored as full "Region - Area - Laboratory" strings; `formatLocation`
// (e.g. getLaboratoryOnly when the org hides Region/Area) maps them to the label the
// user sees, so display, search and sort all work on the visible text.
export const buildSeriesLocations = (data, seriesField, formatLocation = (l) => l) => {
  const byName = new Map();
  if (!Array.isArray(data)) return byName;
  data.forEach((point) => {
    const name = point?.[seriesField];
    if (name === undefined || name === null) return;
    if (!byName.has(name)) byName.set(name, new Set());
    const raw = typeof point.location === 'string' ? point.location.trim() : '';
    const location = raw ? String(formatLocation(raw) || '').trim() : '';
    if (location) byName.get(name).add(location);
  });
  const result = new Map();
  byName.forEach((locations, name) => {
    result.set(name, [...locations].sort(collator.compare));
  });
  return result;
};

export const formatSeriesLocations = (locations) =>
  Array.isArray(locations) ? locations.join(', ') : '';

// Case-insensitive substring match on the name or any of its locations.
export const filterSeriesItems = (items, search) => {
  const term = String(search || '')
    .trim()
    .toLowerCase();
  if (!term) return items;
  return items.filter(
    (item) =>
      String(item.name).toLowerCase().includes(term) ||
      (item.locations || []).some((location) => location.toLowerCase().includes(term))
  );
};

// 'name': natural name order. 'location': first location, then name; items with no
// location go last. Returns a new array.
export const sortSeriesItems = (items, sortBy) => {
  const byName = (a, b) => collator.compare(String(a.name), String(b.name));
  if (sortBy !== 'location') return [...items].sort(byName);
  return [...items].sort((a, b) => {
    const la = formatSeriesLocations(a.locations);
    const lb = formatSeriesLocations(b.locations);
    if (!la !== !lb) return la ? -1 : 1;
    return collator.compare(la, lb) || byName(a, b);
  });
};
